import React, { MouseEvent, useId, useRef } from "react";
import { motion } from "framer-motion";
import { Cpu } from "lucide-react";

const cn = (...classes: Array<string | undefined | false>) => classes.filter(Boolean).join(" ");

interface MagicCardProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  effectColor: string;
  hoverGradientFrom?: string;
  hoverGradientTo?: string;
}

const MagicCardFrame = ({ title, effectColor, hoverGradientFrom, hoverGradientTo, className, children, ...props }: MagicCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const filterId = `magic-card-blur-${id.replace(/:/g, "")}`;
  const tiltThreshold = 12;

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    const card = cardRef.current;
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const relativeX = event.clientX - centerX;
    const relativeY = event.clientY - centerY;

    const pointerX = (relativeX / (rect.width / 2)).toFixed(3);
    const pointerY = (relativeY / (rect.height / 2)).toFixed(3);

    const tiltX = ((relativeY / (rect.height / 2)) * -tiltThreshold).toFixed(3);
    const tiltY = ((relativeX / (rect.width / 2)) * tiltThreshold).toFixed(3);

    card.style.setProperty("--pointer-x", pointerX);
    card.style.setProperty("--pointer-y", pointerY);
    card.style.setProperty("--tilt-x", tiltX);
    card.style.setProperty("--tilt-y", tiltY);
  };

  const handleMouseLeave = () => {
    const card = cardRef.current;
    if (!card) return;
    card.style.setProperty("--pointer-x", "-10");
    card.style.setProperty("--pointer-y", "-10");
    card.style.setProperty("--tilt-x", "0");
    card.style.setProperty("--tilt-y", "0");
  };

  return (
    <article
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={cn("group relative", className)}
      style={{
        ["--pointer-x" as string]: "-10",
        ["--pointer-y" as string]: "-10",
        ["--tilt-x" as string]: "0",
        ["--tilt-y" as string]: "0",
        transform: "perspective(1000px) rotateX(calc(var(--tilt-x) * 1deg)) rotateY(calc(var(--tilt-y) * 1deg))",
        transition: "transform 200ms ease-out",
        willChange: "transform",
      }}
      {...props}
    >
      <div className="pointer-events-none absolute inset-0 z-[1] opacity-0 group-hover:opacity-100 transition-opacity duration-300 overflow-hidden rounded-[inherit]">
        <div
          className="absolute inset-0 grid place-items-center"
          style={{
            transform: "translateZ(0)",
            filter: `url(#${filterId}) saturate(5) brightness(1.3) contrast(1.4)`,
            translate: "calc(var(--pointer-x, -10) * 50cqi) calc(var(--pointer-y, -10) * 50cqh)",
            scale: "3.4",
            willChange: "transform, filter",
          }}
        >
          <div
            className="h-24 w-24 rounded-full"
            style={{ backgroundColor: effectColor }}
          />
        </div>
      </div>

      {hoverGradientFrom && hoverGradientTo ? (
        <div
          className="pointer-events-none absolute inset-0 z-[2] opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-out rounded-[inherit]"
          style={{ backgroundImage: `linear-gradient(135deg, ${hoverGradientFrom}, ${hoverGradientTo})` }}
        />
      ) : null}

      <div className="relative z-[4] h-full">
        {children}
      </div>

      <div
        className="pointer-events-none absolute inset-0 z-[3] rounded-[inherit] border-[3px] border-transparent backdrop-blur-sm backdrop-saturate-[2]"
        style={{
          mask: "linear-gradient(#fff 0 100%) border-box, linear-gradient(#fff 0 100%) padding-box",
          maskComposite: "exclude",
          WebkitMaskComposite: "xor",
        }}
      />

      <svg className="absolute h-0 w-0 overflow-visible opacity-0 pointer-events-none">
        <defs>
          <filter id={filterId} width="500%" height="500%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="28" />
          </filter>
        </defs>
      </svg>
    </article>
  );
};

export default function FeatureSection() {
  return (
    <section id="services" className="py-24 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-5xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 mb-6">
            <Cpu className="w-3.5 h-3.5 text-indigo-600" />
            <span className="text-[10px] font-black uppercase tracking-widest text-indigo-700">Migration Core</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-black mb-6 tracking-tighter leading-[1.1] text-slate-900">
            <span className="block md:whitespace-nowrap">Helping you build a secure</span>
            <span className="italic underline decoration-indigo-200 underline-offset-8">future in Australia.</span>
          </h2>
          <p className="text-slate-600 text-lg max-w-3xl mx-auto leading-relaxed">
            <span className="block">
              We guide skilled professionals, students, and families with expert migration advice,
            </span>
            <span className="block">
              personalised support, and outcome-focused strategy.
            </span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="md:col-span-2"
          >
            <MagicCardFrame
              title="Global Edge Network"
              effectColor="#FF2C17"
              hoverGradientFrom="#FAA729"
              hoverGradientTo="#FF2C17"
              className="h-full bg-white rounded-[2.5rem] p-10 border border-indigo-200 overflow-hidden min-h-[300px] flex flex-col justify-between shadow-xl"
            >
              <div className="relative z-10 w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center text-indigo-700 mb-8 border border-indigo-200 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                <img src="/assets/feature-section/icons/global-edge-network.png" alt="Global Edge Network" className="w-10 h-10 object-contain" loading="lazy" decoding="async" />
              </div>

              <div className="relative z-10">
                <h3 className="text-3xl font-black text-slate-900 group-hover:text-white transition-colors duration-300 mb-4 tracking-tight">Visa outcomes backed by precision and expert case handling.</h3>
                <p className="text-slate-600 group-hover:text-white transition-colors duration-300 leading-relaxed max-w-md">
                  Accurate documentation and personalised migration support drive a 100% success-focused process.
                </p>
              </div>

            </MagicCardFrame>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <MagicCardFrame
              title="Enterprise Security"
              effectColor="#49A2F7"
              hoverGradientFrom="#2654AA"
              hoverGradientTo="#49A2F7"
              className="h-full bg-white border border-emerald-200 rounded-[2.5rem] p-10 overflow-hidden min-h-[300px] flex flex-col justify-between shadow-sm"
            >
              <div className="relative z-10 w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center text-emerald-700 mb-8 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-3">
                <img src="/assets/feature-section/icons/enterprise-security.png" alt="Enterprise Security" className="w-10 h-10 object-contain" loading="lazy" decoding="async" />
              </div>
              <div className="relative z-10">
                <h3 className="text-2xl font-black text-slate-900 group-hover:text-white transition-colors duration-300 mb-4 tracking-tight">10+ pathways across Australian visa streams</h3>
                <p className="text-slate-600 group-hover:text-white transition-colors duration-300 text-sm leading-relaxed">
                  Skilled, student, employer-sponsored, and permanent residency pathways handled end-to-end.
                </p>
              </div>
            </MagicCardFrame>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <MagicCardFrame
              title="5000+ Applications"
              effectColor="#FB8819"
              hoverGradientFrom="#FFB52C"
              hoverGradientTo="#FB8819"
              className="h-full bg-white border border-amber-200 rounded-[2.5rem] p-8 flex flex-col justify-center items-center text-center transition-colors shadow-sm"
            >
              <div className="relative z-10 w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center text-amber-700 mb-6 transition-colors duration-300">
                <img src="/assets/feature-section/icons/applications-5000.png" alt="5000+ Applications" className="w-9 h-9 object-contain" loading="lazy" decoding="async" />
              </div>
              <h4 className="relative z-10 text-lg font-black tracking-tight mb-2 text-slate-900 group-hover:text-white transition-colors duration-300">5000+ Applications</h4>
              <p className="relative z-10 text-xs text-slate-600 group-hover:text-white transition-colors duration-300">Delivered across skilled, student, partner, and regional streams.</p>
            </MagicCardFrame>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <MagicCardFrame
              title="700+ Partners"
              effectColor="#15C232"
              hoverGradientFrom="#38E3A5"
              hoverGradientTo="#62E464"
              className="h-full bg-white border border-blue-200 rounded-[2.5rem] p-8 flex flex-col justify-center items-center text-center transition-colors shadow-sm"
            >
              <div className="relative z-10 w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-700 mb-6 transition-colors duration-300">
                <img src="/assets/feature-section/icons/partners-700.png" alt="700+ Partners" className="w-9 h-9 object-contain" loading="lazy" decoding="async" />
              </div>
              <h4 className="relative z-10 text-lg font-black tracking-tight mb-2 text-slate-900 group-hover:text-white transition-colors duration-300">700+ Partners</h4>
              <p className="relative z-10 text-xs text-slate-600 group-hover:text-white transition-colors duration-300">Trusted institutions and advisors across migration ecosystems.</p>
            </MagicCardFrame>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="md:col-span-1"
          >
            <MagicCardFrame
              title="Personalised Case Strategy"
              effectColor="#FA2F47"
              hoverGradientFrom="#FB667B"
              hoverGradientTo="#FA2F47"
              className="h-full bg-white rounded-[2.5rem] p-10 text-slate-900 overflow-hidden flex flex-col justify-between shadow-xl border border-pink-200"
            >
              <div className="relative z-10 w-14 h-14 rounded-2xl bg-pink-100 flex items-center justify-center text-pink-700 mb-6 border border-pink-200">
                <img src="/assets/feature-section/icons/personalised-case-strategy.png" alt="Personalised Case Strategy" className="w-10 h-10 object-contain" loading="lazy" decoding="async" />
              </div>

              <div className="relative z-10">
                <h3 className="text-2xl font-black mb-2 tracking-tight group-hover:text-white transition-colors duration-300">Personalised Case Strategy</h3>
                <p className="text-slate-600 group-hover:text-white transition-colors duration-300 text-sm">
                  Tailored migration plans built around your profile, goals, and long-term settlement vision.
                </p>
              </div>
            </MagicCardFrame>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
